import { useEffect, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { useIntl } from 'gatsby-plugin-react-intl';
import uuid from 'react-uuid';

// Components
import Link from '../../Core/Link';
import RichText, { fragment } from '../../Core/RichText';
import Blade from '../Blade';
import { Context } from '../../Providers/Blade';
import PCCardSpecList from '../TitleImageWithIconList/PCCardSpecList';
import { isNotEmpty } from '../../../utils';

import {
  Wrapper,
  BladeButton,
  Paragraph,
  Ulist,
  Olist,
  CTAWrapper,
  StyledSpan,
  StyledSup,
  ParagraphContainer,
} from './styles';

const options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <Paragraph>{children}</Paragraph>,
    [BLOCKS.UL_LIST]: (_, children) => <Ulist>{children}</Ulist>,
    [BLOCKS.OL_LIST]: (_, children) => <Olist>{children}</Olist>,
    [INLINES.HYPERLINK]: (node, children) => {
      const { data } = node;
      return (
        <Link className="rich-text-link" to={data.uri} fontSize="inherit">
          {children}
        </Link>
      );
    },
  },
};

const IndividualBlade = ({ data }) => {
  if (!data) return null;
  const { state, updateBladeState } = useContext(Context);

  const intl = useIntl();

  const {
    id,
    component,
    linkText,
    type,
    ctaButtonSize,
    // closeText,
    productCards,
    bodyCopy,
    ctas,
    introductionBody,
    asteriskBody,
    daggerBody,
    sectionSignBody,
    numbersBody,
    footerBody,
  } = data;

  const sanitizedBladeName = useMemo(
    () => `${uuid()}-${linkText.replace(/\s+/g, '-').toLowerCase()}`,
    [],
  );

  useEffect(() => {
    const bladeState = {};
    bladeState[sanitizedBladeName] = false;
    updateBladeState(bladeState);
  }, []);

  const handleClick = () => {
    const bladeState = {};
    bladeState[sanitizedBladeName] = !state[sanitizedBladeName];
    updateBladeState(bladeState);
  };

  const prependSymbol = (index, symbol) => {
    switch (symbol) {
      case 'asterisk':
        return (
          <StyledSpan id={symbol}>
            <span className="sr-only">Footnote</span>*
          </StyledSpan>
        );
      case 'dagger':
        return (
          <StyledSup id={symbol}>
            <span className="sr-only">Footnote</span>
            {['†', '‡'][index]}
          </StyledSup>
        );
      case 'section-sign':
        return (
          <StyledSup id={symbol}>
            <span className="sr-only">Footnote</span>§
          </StyledSup>
        );
      case 'number':
        return (
          <StyledSup id={`${symbol}-${index + 1}`}>
            <span className="sr-only">Footnote</span>
            {`${index + 1}`}
          </StyledSup>
        );
      default:
        return '';
    }
  };

  const formatCopy = (copy, index, key, symbol) => {
    const opts = {
      renderNode: {
        [BLOCKS.PARAGRAPH]: (_, children) => {
          return isNotEmpty(children) ? (
            <Paragraph>{children}</Paragraph>
          ) : null;
        },
        [BLOCKS.UL_LIST]: (_, children) => <Ulist>{children}</Ulist>,
        [BLOCKS.OL_LIST]: (_, children) => <Olist>{children}</Olist>,
        [INLINES.HYPERLINK]: (node, children) => {
          const { data: nodeData } = node;
          return (
            <Link to={nodeData.uri} fontSize="inherit">
              {children}
            </Link>
          );
        },
      },
    };
    return (
      <ParagraphContainer key={key}>
        {prependSymbol(index, symbol)}
        <RichText document={copy} options={opts} />
      </ParagraphContainer>
    );
  };

  return (
    <Wrapper key={id} className={component}>
      <BladeButton
        isOpen={state[sanitizedBladeName]}
        className={`${component} ${type} ${ctaButtonSize}`}
        onClick={() => handleClick()}
        aria-label={
          intl.locale === 'en'
            ? `Open ${linkText} modal`
            : `Ouvrez ${linkText} modal`
        }
      >
        {linkText}
      </BladeButton>
      <Blade
        title={linkText}
        isOpen={state[sanitizedBladeName]}
        component={component}
      >
        {bodyCopy && <RichText document={bodyCopy} options={options} />}
        {introductionBody &&
          introductionBody.map((rte, index) => {
            return formatCopy(rte.paragraph, index, rte.id, '');
          })}
        {asteriskBody &&
          asteriskBody.map((rte, index) => {
            return formatCopy(rte.paragraph, index, rte.id, 'asterisk');
          })}
        {daggerBody &&
          daggerBody.map((rte, index) => {
            return formatCopy(rte.paragraph, index, rte.id, 'dagger');
          })}
        {sectionSignBody &&
          sectionSignBody.map((rte, index) => {
            return formatCopy(rte.paragraph, index, rte.id, 'section-sign');
          })}
        {numbersBody &&
          numbersBody.map((rte, index) => {
            return formatCopy(rte.paragraph, index, rte.id, 'number');
          })}
        {footerBody &&
          footerBody.map((rte, index) => {
            return formatCopy(rte.paragraph, index, rte.id, '');
          })}
        {productCards &&
          productCards.map(card => {
            return (
              <PCCardSpecList key={id} data={card} bladeType={component} />
            );
          })}
        {ctas && (
          <CTAWrapper>
            {ctas &&
              ctas.map(cta => {
                if (cta.hideFrench === true) return null;
                return (
                  <Link key={cta.id} data={cta}>
                    <RichText document={cta.label} options={fragment} />
                  </Link>
                );
              })}
          </CTAWrapper>
        )}
      </Blade>
    </Wrapper>
  );
};

IndividualBlade.propTypes = {
  data: PropTypes.shape().isRequired,
};

export default IndividualBlade;
